import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Select } from 'primeng/select';
import {
    CategoryType,
    GameTemplate,
    SiteTemplate,
    WinMode,
} from '@/lib-backend';
import { SiteTemplateService } from '@/app/services/site-template.service';
import {
    ART_GROUP,
    ART_PROP,
    ProductAssetService,
    readArtProperty,
} from '@/app/services/product-asset.service';
import { SharedContentService } from '@/app/services/shared-content.service';
import { NotificationService } from '@/app/services/notification.service';
import { AuthService } from '@/app/services/auth.service';
import { BrandKit, BrandKitService } from '@/app/services/brand-kit.service';
import {
    AiAssistApiError,
    AiAssistService,
    BaseArtResult,
    FinalizeCandidate,
    FinalizeResult,
} from '@/app/services/ai-assist.service';
import { ImagePreviewService } from '@/app/services/image-preview.service';
import {
    hashPathToken,
    sharedContentPreviewUrl,
} from '@/app/utils/shared-content-url';

type Plate = 'icon' | 'banner';
const PLATES: Plate[] = ['icon', 'banner'];
const SAMPLES = 2;

/** Resolved generation context for one compatible reference site template. */
interface RefOption {
    siteTemplateId: string;
    name: string;
    brandKit: BrandKit | null;
    iconPlate: string | null; // stored base-art icon (relative URL) or null
    bannerPlate: string | null;
}

interface PlateState {
    status: 'idle' | 'working' | 'done' | 'error';
    statusText: string;
    candidates: FinalizeCandidate[];
    selectedUrl: string | null;
    error: string | null;
}

/** The chosen finalized URLs handed back to the host on save. */
export interface FinalizedArtApplied {
    iconUrl: string;
    bannerUrl: string;
}

/**
 * Generate the game-template **default** icon + banner (title rendered in) via
 * the finalize chain, borrowing a reference site template's style. If that
 * template already has base art it is used as the plate; otherwise base art is
 * generated first (auto-chain Contract 1 → Contract 2). The chosen result is
 * saved as one finalized `iconBanner` variant (name = title) for reuse, and the
 * stored URLs are emitted so the host can point the template defaults at them.
 */
@Component({
    selector: 'app-ai-finalize-game-art',
    standalone: true,
    imports: [
        FormsModule,
        NgClass,
        Button,
        Dialog,
        Message,
        ProgressSpinner,
        Select,
    ],
    templateUrl: './ai-finalize-game-art.html',
})
export class AiFinalizeGameArt {
    readonly visible = input.required<boolean>();
    readonly template = input<GameTemplate | null>(null);
    /**
     * Realm mode: lock generation to one site template (the realm's) instead of
     * letting the operator pick a reference, and carry the realm id into the
     * finalize call. Null = template-default mode (free reference pick, no realm).
     */
    readonly fixedSiteTemplateId = input<string | null>(null);
    readonly realmId = input<string | null>(null);
    /**
     * Realm-level title override. When set (non-null), it is the name the
     * operator entered for this game on the realm and takes precedence over the
     * template title for the rendered title, generation context and the saved
     * variant name. Null = use the template title (template-default mode).
     */
    readonly titleOverride = input<string | null>(null);

    readonly visibleChange = output<boolean>();
    readonly applied = output<FinalizedArtApplied>();

    private readonly siteTemplateService = inject(SiteTemplateService);
    private readonly productAsset = inject(ProductAssetService);
    private readonly sharedContent = inject(SharedContentService);
    private readonly notification = inject(NotificationService);
    private readonly auth = inject(AuthService);
    private readonly brandKitService = inject(BrandKitService);
    private readonly aiAssist = inject(AiAssistService);
    protected readonly preview = inject(ImagePreviewService);

    protected readonly preparing = signal(false);
    protected readonly options = signal<RefOption[]>([]);
    protected readonly refSiteTemplateId = signal<string | null>(null);
    protected readonly saving = signal(false);

    protected readonly icon = signal<PlateState>(this.emptyPlate());
    protected readonly banner = signal<PlateState>(this.emptyPlate());

    private abort: AbortController | null = null;

    protected readonly canManage = computed(() => this.auth.isGlobalAdmin());

    /** The name to render/generate with: realm override if set, else template. */
    protected readonly effectiveTitle = computed(
        () => this.titleOverride() ?? this.template()?.Title ?? '',
    );

    private readonly compatibleCategory = computed<CategoryType | null>(() => {
        switch (this.template()?.GameKind?.WinMode) {
            case WinMode.SkillBased:
                return CategoryType.SkillBasedGames;
            case WinMode.ChanceBased:
                return CategoryType.LuckyBasedGames;
            default:
                return null;
        }
    });

    protected readonly refOptions = computed(() =>
        this.options().map((o) => ({ label: o.name, value: o.siteTemplateId })),
    );

    protected readonly selectedRef = computed<RefOption | null>(
        () =>
            this.options().find(
                (o) => o.siteTemplateId === this.refSiteTemplateId(),
            ) ?? null,
    );

    /** Null when generation is possible, else the reason. */
    protected readonly blockReason = computed<string | null>(() => {
        if (!this.canManage()) return 'Only a global admin can generate art.';
        if (this.compatibleCategory() === null) {
            return "This game's win mode is unresolved — no compatible site template.";
        }
        const ref = this.selectedRef();
        if (!ref) return 'Select a reference site template.';
        const canIcon = !!ref.iconPlate || this.hasRefs(ref);
        const canBanner = !!ref.bannerPlate || this.hasRefs(ref);
        if (!canIcon || !canBanner) {
            return 'The reference template has neither base art nor brand references to generate from.';
        }
        if (this.aiAssist.status() === 'down') {
            return 'AI Assist service is unavailable.';
        }
        return null;
    });

    protected readonly working = computed(
        () =>
            this.icon().status === 'working' ||
            this.banner().status === 'working',
    );

    protected readonly canSave = computed(
        () =>
            !this.saving() &&
            !this.working() &&
            !!this.icon().selectedUrl &&
            !!this.banner().selectedUrl,
    );

    constructor() {
        effect(() => {
            if (this.visible()) {
                void this.prepare();
                if (this.aiAssist.status() === 'unknown') {
                    void this.aiAssist.refreshHealth();
                }
                void this.aiAssist.refreshVendorBalances();
            } else {
                this.cancelPolling();
            }
        });
    }

    /** Realm mode hides the reference picker (the site template is fixed). */
    protected readonly lockedRef = computed(() => !!this.fixedSiteTemplateId());

    private hasRefs(ref: RefOption): boolean {
        return !!ref.brandKit && ref.brandKit.references.length > 0;
    }

    protected plate(asset: Plate): PlateState {
        return asset === 'icon' ? this.icon() : this.banner();
    }

    // ── Prepare: compatible reference templates ──────────────────────────────

    private async prepare(): Promise<void> {
        this.reset();
        const template = this.template();
        const wanted = this.compatibleCategory();
        if (!template || wanted === null) return;

        this.preparing.set(true);
        try {
            if (this.siteTemplateService.templates().length === 0) {
                await this.siteTemplateService.loadTemplates();
            }
            const fixed = this.fixedSiteTemplateId();
            const compatible = this.siteTemplateService
                .templates()
                .filter((st: SiteTemplate) => st.CategoryType === wanted)
                // Realm mode: only the realm's own site template.
                .filter((st: SiteTemplate) => !fixed || st.SiteTemplateId === fixed)
                .slice()
                .sort((a, b) => a.Name.localeCompare(b.Name));

            const options = await Promise.all(
                compatible.map(async (st) => {
                    const [brandKit, art] = await Promise.all([
                        this.brandKitService.resolve(st.SiteTemplateId),
                        this.productAsset.getGameTemplateBaseArt(
                            st.SiteTemplateId,
                            template.Id,
                        ),
                    ]);
                    const option: RefOption = {
                        siteTemplateId: st.SiteTemplateId,
                        name: st.Name,
                        brandKit,
                        iconPlate: art
                            ? readArtProperty(art, ART_PROP.Icon)
                            : null,
                        bannerPlate: art
                            ? readArtProperty(art, ART_PROP.Banner)
                            : null,
                    };
                    return option;
                }),
            );
            if (!this.visible()) return;
            this.options.set(options);
            // Default to the first template that can fully generate — prefer one
            // that already has both base plates, else the first with refs.
            const ready = options.find(
                (o) =>
                    (o.iconPlate || this.hasRefs(o)) &&
                    (o.bannerPlate || this.hasRefs(o)),
            );
            this.refSiteTemplateId.set(
                (ready ?? options[0])?.siteTemplateId ?? null,
            );
        } finally {
            this.preparing.set(false);
        }
    }

    // ── Generate ─────────────────────────────────────────────────────────────

    protected generate(): void {
        if (this.blockReason() || this.working()) return;
        this.cancelPolling();
        this.abort = new AbortController();
        for (const asset of PLATES) {
            void this.runPlate(asset, this.abort.signal);
        }
    }

    protected regenerate(asset: Plate): void {
        if (this.blockReason()) return;
        if (!this.abort) this.abort = new AbortController();
        void this.runPlate(asset, this.abort.signal);
    }

    private async runPlate(asset: Plate, signal: AbortSignal): Promise<void> {
        const template = this.template();
        const ref = this.selectedRef();
        if (!template || !ref) return;
        const set = asset === 'icon' ? this.icon : this.banner;
        set.set({
            ...this.emptyPlate(),
            status: 'working',
            statusText: 'Resolving base plate…',
        });
        try {
            const plate = await this.resolvePlate(asset, ref, template, signal);
            if (!plate) {
                set.update((s) => ({
                    ...s,
                    status: 'error',
                    error: `Could not obtain a ${asset} base plate.`,
                }));
                return;
            }
            set.update((s) => ({ ...s, statusText: `Finalizing ${asset}…` }));
            const accepted = await this.aiAssist.finalizeImage({
                jobId: crypto.randomUUID(),
                realmId: this.realmId(),
                basePlateUrl: plate.url,
                basePlateB64: plate.b64,
                assetType: asset,
                title: this.effectiveTitle(),
                samples: SAMPLES,
            });
            const done = await this.aiAssist.awaitJob(accepted.job_id, {
                signal,
            });
            const result = done.result as unknown as FinalizeResult | null;
            const candidates = result?.candidates ?? [];
            set.update((s) => ({
                ...s,
                status: 'done',
                statusText: '',
                candidates,
                selectedUrl: candidates[0]?.url ?? null,
            }));
        } catch (err) {
            if (signal.aborted) return;
            set.update((s) => ({
                ...s,
                status: 'error',
                error: this.friendlyError(err),
            }));
        }
    }

    /**
     * Get the text-free plate for `asset`: the reference template's stored base
     * art (fetched inline as base64) if present, else auto-generate it via
     * Contract 1 and use the candidate URL directly. Returns the form the
     * finalize call should use (exactly one of url / b64).
     */
    private async resolvePlate(
        asset: Plate,
        ref: RefOption,
        template: GameTemplate,
        signal: AbortSignal,
    ): Promise<{ url?: string; b64?: string } | null> {
        const stored = asset === 'icon' ? ref.iconPlate : ref.bannerPlate;
        if (stored) {
            const b64 = await this.fetchAsB64(sharedContentPreviewUrl(stored));
            if (b64) return { b64 };
        }
        // No usable base art — generate a plate first (Contract 1), no realm.
        const kit = ref.brandKit;
        if (!kit || kit.references.length === 0) return null;
        const set = asset === 'icon' ? this.icon : this.banner;
        set.update((s) => ({ ...s, statusText: `Generating ${asset} plate…` }));
        const accepted = await this.aiAssist.generateBaseArt({
            jobId: crypto.randomUUID(),
            templateId: ref.siteTemplateId,
            version: kit.version ?? '',
            referenceImagesB64: kit.references.map((r) => r.b64),
            assetType: asset,
            styleDescriptor: kit.style,
            gameTemplateId: template.Id,
            gameKind: template.GameKind?.Name ?? template.GameKindId,
            gameMeta: {
                name: this.effectiveTitle(),
                description: template.Description,
            },
            mode: 'auto',
            prompt: null,
            operatorNote: null,
            samples: 1,
        });
        const done = await this.aiAssist.awaitJob(accepted.job_id, { signal });
        const result = done.result as BaseArtResult | null;
        const url = result?.candidates[0]?.url;
        return url ? { url } : null;
    }

    protected pick(asset: Plate, candidate: FinalizeCandidate): void {
        const set = asset === 'icon' ? this.icon : this.banner;
        set.update((s) => ({ ...s, selectedUrl: candidate.url }));
    }

    protected isSelected(asset: Plate, url: string): boolean {
        return this.plate(asset).selectedUrl === url;
    }

    // ── Save ─────────────────────────────────────────────────────────────────

    protected async save(): Promise<void> {
        const template = this.template();
        const ref = this.selectedRef();
        const iconUrl = this.icon().selectedUrl;
        const bannerUrl = this.banner().selectedUrl;
        if (
            !template ||
            !ref ||
            !iconUrl ||
            !bannerUrl ||
            !this.canSave()
        ) {
            return;
        }
        this.saving.set(true);
        try {
            const variantId = crypto.randomUUID();
            const storedIcon = await this.rehost(
                template.Id,
                ref.siteTemplateId,
                variantId,
                'icon',
                iconUrl,
            );
            const storedBanner = await this.rehost(
                template.Id,
                ref.siteTemplateId,
                variantId,
                'banner',
                bannerUrl,
            );
            if (!storedIcon || !storedBanner) {
                this.notification.show({
                    severity: 'error',
                    summary: 'Could not save the generated art',
                });
                return;
            }
            const { result } =
                await this.productAsset.addGameTemplateFinalizedArt(
                    ref.siteTemplateId,
                    template.Id,
                    [
                        { Name: ART_PROP.Group, Value: ART_GROUP.IconBanner },
                        { Name: ART_PROP.Icon, Value: storedIcon },
                        { Name: ART_PROP.Banner, Value: storedBanner },
                        { Name: ART_PROP.Name, Value: this.effectiveTitle() },
                    ],
                );
            if (result === 'Ok') {
                this.notification.show({
                    severity: 'success',
                    summary: 'Default art generated',
                    detail: 'Saved as a finalized variant and applied as the template default.',
                });
                this.applied.emit({
                    iconUrl: storedIcon,
                    bannerUrl: storedBanner,
                });
                this.onVisibleChange(false);
            } else {
                this.notification.show({
                    severity: 'error',
                    summary: 'Save failed',
                    detail:
                        result === 'SiteTemplateNotFound'
                            ? 'The reference site template no longer exists.'
                            : 'Please try again.',
                });
            }
        } finally {
            this.saving.set(false);
        }
    }

    private async rehost(
        gameTemplateId: string,
        siteTemplateId: string,
        variantId: string,
        slot: Plate,
        candidateUrl: string,
    ): Promise<string | null> {
        const hashed = await hashPathToken(siteTemplateId);
        const path = `products/${gameTemplateId}/${hashed}/${variantId}-${slot}`;
        return this.sharedContent.rehostImageUrl(path, candidateUrl);
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    protected onVisibleChange(value: boolean): void {
        if (!value) this.cancelPolling();
        this.visibleChange.emit(value);
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    private cancelPolling(): void {
        this.abort?.abort();
        this.abort = null;
    }

    private reset(): void {
        this.cancelPolling();
        this.options.set([]);
        this.refSiteTemplateId.set(null);
        this.icon.set(this.emptyPlate());
        this.banner.set(this.emptyPlate());
        this.saving.set(false);
    }

    private emptyPlate(): PlateState {
        return {
            status: 'idle',
            statusText: '',
            candidates: [],
            selectedUrl: null,
            error: null,
        };
    }

    /** Fetch a same-origin shared-content image and return its base64 body. */
    private async fetchAsB64(url: string): Promise<string | null> {
        try {
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) return null;
            const bytes = new Uint8Array(await res.arrayBuffer());
            let binary = '';
            for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        } catch {
            return null;
        }
    }

    private friendlyError(err: unknown): string {
        if (!(err instanceof AiAssistApiError)) {
            return 'Could not reach the AI Assist service.';
        }
        switch (err.code) {
            case 'vendor_timeout':
                return 'Generation took too long.';
            case 'vendor_unavailable':
                return 'The image model is temporarily unavailable.';
            case 'content_moderated':
                return 'Blocked by the content filter.';
            case 'invalid_references':
                return 'Reference images could not be used.';
            case 'invalid_request':
                return err.message ?? 'The request was rejected.';
            default:
                return err.message ?? 'Something went wrong.';
        }
    }
}
